import React, { useCallback, useEffect, useMemo, useState } from 'react';
import InboxTabs from '../../components/admin/InboxTabs';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { ArrowLeft, MailWarning, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Checkbox } from '../../components/ui/checkbox';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../components/ui/alert-dialog';
import { EmailStatusBadge, AudienceBadge, purposeLabel, fmtWhen } from '../../components/admin/email/emailMeta';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FailedRow = ({ e, checked, onToggle, onRetryOne }) => (
  <div className="px-4 py-3 border-t border-zinc-100 flex gap-3" data-testid={`failed-email-${e.id}`}>
    <div className="pt-1">
      <Checkbox checked={checked} disabled={!e.retryable} onCheckedChange={onToggle} aria-label={`Select email to ${e.to}`} data-testid={`failed-email-select-${e.id}`} />
    </div>
    <div className="min-w-0 flex-1">
      <div className="flex flex-wrap items-center gap-2">
        <AudienceBadge audience={e.audience} />
        <EmailStatusBadge status={e.status} />
        <span className="text-[11px] text-zinc-400">{fmtWhen(e.created_at)}</span>
      </div>
      <p className="text-sm font-semibold text-zinc-900 mt-1 break-words">{e.subject}</p>
      <p className="text-xs text-zinc-600 break-all">To {e.to} · {purposeLabel(e.purpose)}</p>
      <p className="text-xs text-red-600 mt-1 break-words" data-testid={`failed-email-reason-${e.id}`}>{e.error || 'No reason provided'}</p>
      {!e.retryable && <p className="text-xs text-zinc-500 mt-1" data-testid={`failed-email-blocked-${e.id}`}>Can't retry: {e.retry_block_reason}</p>}
    </div>
    {e.retryable && <Button size="sm" variant="outline" className="self-start" onClick={onRetryOne} data-testid={`failed-email-retry-${e.id}`}>Retry</Button>}
  </div>
);

export default function AdminFailedEmailsPage() {
  const { token } = useAuth();
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [items, setItems] = useState(null);
  const [selected, setSelected] = useState([]);
  const [confirming, setConfirming] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    axios.get(`${API_URL}/api/admin/emails/failed`, { headers }).then((r) => { setItems(r.data); setSelected([]); })
      .catch(() => { toast.error('Could not load failed emails'); setItems([]); });
  }, [headers]);
  useEffect(() => { load(); }, [load]);

  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const chosen = (items || []).filter((e) => (confirming || []).includes(e.id));

  const retry = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API_URL}/api/admin/emails/retry`, { ids: confirming, confirmed_recipients: [...new Set(chosen.map((e) => e.to))] }, { headers });
      const failed = r.data.results.filter((x) => !x.ok);
      if (r.data.sent) toast.success(`${r.data.sent} email${r.data.sent === 1 ? '' : 's'} sent again`);
      if (failed.length) toast.error(`${failed.length} could not be sent: ${failed[0].error || 'see the list'}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Retry failed');
    } finally {
      setBusy(false); setConfirming(null); load();
    }
  };

  return (
    <div className="max-w-[1000px] mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-4" data-testid="failed-emails-page">
      <InboxTabs />
      <div className="flex items-center gap-3">
        <Link to="/admin/dashboard" className="text-zinc-500 hover:text-zinc-900" data-testid="back-to-dashboard"><ArrowLeft className="w-5 h-5" /></Link>
        <div className="w-10 h-10 rounded-xl bg-red-50 flex items-center justify-center"><MailWarning className="w-5 h-5 text-red-600" /></div>
        <div className="flex-1 min-w-0">
          <h1 className="text-lg font-bold text-zinc-900">Review failed emails</h1>
          <p className="text-xs text-zinc-500">Check each recipient and reason before sending again. Retrying resends the same message only; it never creates leads or bookings.</p>
        </div>
        <Button variant="outline" size="sm" onClick={load} data-testid="failed-emails-refresh"><RefreshCw className="w-4 h-4 mr-1" /> Refresh</Button>
      </div>
      <div className="flex items-center gap-3">
        <Button size="sm" disabled={!selected.length} onClick={() => setConfirming(selected)} className="bg-orange-600 hover:bg-orange-700" data-testid="failed-emails-retry-selected">Retry selected ({selected.length})</Button>
      </div>
      <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden" data-testid="failed-emails-list">
        {items === null && <p className="p-6 text-sm text-zinc-500">Loading…</p>}
        {items?.length === 0 && <p className="p-6 text-sm text-zinc-500" data-testid="failed-emails-empty">No failed emails to review.</p>}
        {items?.map((e) => <FailedRow key={e.id} e={e} checked={selected.includes(e.id)} onToggle={() => toggle(e.id)} onRetryOne={() => setConfirming([e.id])} />)}
      </div>

      <AlertDialog open={!!confirming} onOpenChange={(o) => !o && !busy && setConfirming(null)}>
        <AlertDialogContent data-testid="failed-emails-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Send {chosen.length} email{chosen.length === 1 ? '' : 's'} again?</AlertDialogTitle>
            <AlertDialogDescription>These recipients will receive the original message again:</AlertDialogDescription>
          </AlertDialogHeader>
          <ul className="max-h-64 overflow-y-auto space-y-2 text-sm" data-testid="failed-emails-confirm-list">
            {chosen.map((e) => (
              <li key={e.id} className="border border-zinc-200 rounded-lg p-2">
                <div className="flex items-center gap-2"><AudienceBadge audience={e.audience} /><span className="font-semibold break-all">{e.to}</span></div>
                <p className="text-xs text-zinc-600 mt-0.5 break-words">{e.subject}</p>
              </li>
            ))}
          </ul>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy} data-testid="failed-emails-confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction disabled={busy} onClick={(ev) => { ev.preventDefault(); retry(); }} className="bg-orange-600 hover:bg-orange-700" data-testid="failed-emails-confirm-send">{busy ? 'Sending…' : 'Send again'}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
